import { useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { useHistory, useRouteMatch } from 'react-router-dom';

import type { Student } from '../../../api/types/student';
import env from '../../../env';
import { useIsHubAuthenticated } from '../../app-shell/useIsHubAuthenticated';
import { userSignout } from '../../legacy/actions';
import api from '../../legacy/api';
import { PATH_HUB_ADD_STUDENT, PATH_HUB_MANAGE_SERVICE, PATH_HUB_SUPPORT } from '../../legacy/pagePath';

export type AccountHolder = {
  firstName: string;
  lastName: string;
  /** Role label shown beneath the account holder's name (e.g. "Admin"). */
  role: string;
};

// Container hook for the account switcher. Resolves the account holder and the
// list of students from hub's data layer and exposes the menu actions.
//
// Switching is intentionally a no-op for now. The eventual behaviour is to
// redirect to the Student Hub site, passing the selected student's details as
// URL params — see `onSelectStudent` for the seam.
export const useAccountSwitcher = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const isHubAuthenticated = useIsHubAuthenticated();

  const { data: accountDetails, isLoading: isLoadingAccount } = api.account.details.useQuery(undefined, {
    enabled: isHubAuthenticated,
  });
  const { data: students, isLoading: isLoadingStudents } = api.student.all.useQuery(undefined, {
    enabled: isHubAuthenticated,
  });

  const accountHolder: AccountHolder | null = accountDetails
    ? { firstName: accountDetails.firstName, lastName: accountDetails.lastName, role: 'Admin' }
    : null;

  const signOut = useCallback(() => {
    dispatch(userSignout());
  }, [dispatch]);

  const onSelectStudent = useCallback((student: Student) => {
    const url = new URL(env.REACT_APP_STUDENT_HUB);
    url.searchParams.set('studentId', student.studentId);
    window.open(url.toString(), '_self');
  }, []);

  const onAddStudent = useCallback(() => {
    history.push(PATH_HUB_ADD_STUDENT);
  }, [history]);

  const onSupport = useCallback(() => {
    history.push(PATH_HUB_SUPPORT);
  }, [history]);

  return {
    accountHolder,
    students: students ?? [],
    // A disabled react-query stays in status "loading" forever (v4 doesn't
    // distinguish "disabled" from "pending"), so gate on auth too, or an
    // anonymous viewer would see the loading skeleton indefinitely instead of
    // the component resolving to null.
    isLoading: isHubAuthenticated && (isLoadingAccount || isLoadingStudents),
    signOut,
    onSelectStudent,
    onAddStudent,
    onSupport,
    isAddStudentActive: Boolean(useRouteMatch({ path: PATH_HUB_ADD_STUDENT, exact: false })),
    isSupportActive: Boolean(useRouteMatch({ path: PATH_HUB_MANAGE_SERVICE, exact: false })),
  };
};
